/**
 * Map extracted BOM lines to canonical BomItem records.
 */
import type { BomItem, SCMCategory } from '../../types/supplyChain';
import { SCM_CATEGORIES } from '../../types/supplyChain';
import type { ExtractedBomLine } from './extractBomFromPdf';

export interface BomFromExtractedOptions {
  /** Prefix for generated ids. */
  idPrefix?: string;
  /** ISO timestamp for provenance notes. */
  now?: () => string;
  /** Linked SiteDocument id. */
  documentId?: string;
}

function generateBomId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeCategory(cat: SCMCategory | undefined): SCMCategory {
  if (cat && (SCM_CATEGORIES as readonly string[]).includes(cat)) return cat;
  return 'power-conversion';
}

export function bomItemsFromExtracted(
  lines: readonly ExtractedBomLine[],
  options: BomFromExtractedOptions = {},
): BomItem[] {
  const prefix = options.idPrefix ?? 'bom-upload';
  const docNote = options.documentId ? ` [doc:${options.documentId}]` : '';

  return lines.map((line) => {
    const qty = line.quantity ?? 1;
    const unitCost = line.unitCostUsd ?? null;
    const category = normalizeCategory(line.category);
    return {
      id: generateBomId(prefix),
      category,
      name: line.name,
      spec: line.spec ?? '',
      quantity: qty,
      unit: line.unit ?? 'each',
      status: 'planned' as const,
      unitCostUsd: unitCost,
      totalCostUsd: unitCost != null ? qty * unitCost : null,
      priceSource: unitCost != null ? 'manual' as const : 'industry-baseline' as const,
      leadTimeMonthsLow: line.leadTimeMonthsLow ?? 6,
      leadTimeMonthsHigh: line.leadTimeMonthsHigh ?? 12,
      leadTimeSource: 'industry-baseline' as const,
      criticality: 'standard' as const,
      substitutability: 'commodity' as const,
      derivedFrom: 'uploaded-bom' as const,
      ...(docNote ? { notes: `Uploaded BoM${docNote}` } : {}),
    };
  });
}
